import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;   // e.g., https://payyug.in/api
const BASE_URL = process.env.REACT_APP_BASE_URL; // e.g., https://payyug.in

// Helper: Get Authorization Header
const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("authToken");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (isMultipart) headers["Content-Type"] = "multipart/form-data";
  return { headers };
};

/* ------------------------- 1️⃣ Fetch Customer KYC List ------------------------- */
export const fetchCustomerKyc = createAsyncThunk(
  "customerKyc/fetchCustomerKyc",
  async (_, { rejectWithValue }) => {
    try {
      const superAdminId = localStorage.getItem("userid") || 1;
      const response = await axios.get(
        `${API_URL}/customers?superAdminId=${superAdminId}`,
        getAuthHeaders()
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch customer KYC"
      );
    }
  }
);

/* ------------------------- 2️⃣ Upload KYC ------------------------- */
export const uploadCustomerKyc = createAsyncThunk(
  "customerKyc/uploadCustomerKyc",
  async ({ userId, docName, status, file, createdBy, createdId }, { rejectWithValue }) => {
    try {
      if (!file) return rejectWithValue("No file selected for upload!");

      const formData = new FormData();
      formData.append("userId", userId || localStorage.getItem("userid") || "1");
      formData.append("docName", docName || "Default Doc");
      formData.append("status", status || "Pending");
      formData.append("createdBy", createdBy || localStorage.getItem("username") || "System");
      formData.append("createdId", createdId || localStorage.getItem("userid") || "0");
      formData.append("file", file);

      if (process.env.NODE_ENV === "development") {
        console.log("📤 Uploading KYC with:");
        formData.forEach((value, key) => console.log(key, value));
      }

      const response = await axios.post(
        `${BASE_URL}/api/upload-loan-kyc`,
        formData,
        getAuthHeaders(true)
      );

      return response.data;
    } catch (error) {
      console.error("❌ Upload failed:", error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data || error.message || "Failed to upload KYC"
      );
    }
  }
);

/* ------------------------- 3️⃣ Fetch Uploaded KYC ------------------------- */
export const fetchUploadedKyc = createAsyncThunk(
  "customerKyc/fetchUploadedKyc",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/get-loan-kyc?userId=${userId}`,
        getAuthHeaders()
      );

      if (process.env.NODE_ENV === "development") {
        console.log("Fetched uploaded KYC for user", userId, response.data);
      }

      const documents = response.data.documents || [];
      return { userId, data: documents };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch uploaded KYC"
      );
    }
  }
);

/* ------------------------- 4️⃣ Approve KYC Document ------------------------- */
/* ------------------------- 4️⃣ Approve KYC Document ------------------------- */
export const approveKycDocument = createAsyncThunk(
  "customerKyc/approveKycDocument",
  async ({ userId, docId }, { rejectWithValue }) => {
    try {
      await axios.put(`${API_URL}/kyc-docs/${docId}/approve`, {}, getAuthHeaders());
      return { userId, docId, status: "Approved" };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to approve document"
      );
    }
  }
);

/* ------------------------- 5️⃣ Reject KYC Document ------------------------- */
export const rejectKycDocument = createAsyncThunk(
  "customerKyc/rejectKycDocument",
  async ({ userId, docId }, { rejectWithValue }) => {
    try {
      await axios.put(`${API_URL}/kyc-docs/${docId}/reject`, {}, getAuthHeaders());
      return { userId, docId, status: "Rejected" };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to reject document"
      );
    }
  }
);


/* ------------------------- 6️⃣ Delete KYC Document ------------------------- */
export const deleteKycDocument = createAsyncThunk(
  "customerKyc/deleteKycDocument",
  async ({ userId, docId }, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/loan-kyc/${docId}`, getAuthHeaders());
      return { userId, docId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to delete document"
      );
    }
  }
);

/* ------------------------- Slice ------------------------- */
const CustomerKycSlice = createSlice({
  name: "customerKyc",
  initialState: {
    list: [],
    uploadedKyc: {},
    loadingCustomers: false,
    loadingUpload: false,
    loadingUploadedKyc: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      /* Fetch customers */
      .addCase(fetchCustomerKyc.pending, (state) => {
        state.loadingCustomers = true;
        state.error = null;
      })
      .addCase(fetchCustomerKyc.fulfilled, (state, action) => {
        state.loadingCustomers = false;
        state.list = action.payload;
      })
      .addCase(fetchCustomerKyc.rejected, (state, action) => {
        state.loadingCustomers = false;
        state.error = action.payload;
      })

      /* Upload KYC */
      .addCase(uploadCustomerKyc.pending, (state) => {
        state.loadingUpload = true;
        state.error = null;
      })
      .addCase(uploadCustomerKyc.fulfilled, (state) => {
        state.loadingUpload = false;
      })
      .addCase(uploadCustomerKyc.rejected, (state, action) => {
        state.loadingUpload = false;
        state.error = action.payload;
      })

      /* Fetch uploaded KYC for specific user */
      .addCase(fetchUploadedKyc.pending, (state) => {
        state.loadingUploadedKyc = true;
        state.error = null;
      })
      .addCase(fetchUploadedKyc.fulfilled, (state, action) => {
        state.loadingUploadedKyc = false;
        state.uploadedKyc = {
          ...state.uploadedKyc,
          [action.payload.userId]: action.payload.data,
        };
      })
      .addCase(fetchUploadedKyc.rejected, (state, action) => {
        state.loadingUploadedKyc = false;
        state.error = action.payload;
      })

      /* Approve Document */
      .addCase(approveKycDocument.fulfilled, (state, action) => {
        const { userId, docId, status } = action.payload;
        if (state.uploadedKyc[userId]) {
          state.uploadedKyc[userId] = state.uploadedKyc[userId].map((doc) =>
            doc.id === docId ? { ...doc, status } : doc
          );
        }
      })

      /* Reject Document */
      .addCase(rejectKycDocument.fulfilled, (state, action) => {
        const { userId, docId, status } = action.payload;
        if (state.uploadedKyc[userId]) {
          state.uploadedKyc[userId] = state.uploadedKyc[userId].map((doc) =>
            doc.id === docId ? { ...doc, status } : doc
          );
        }
      })

      /* Delete Document */
      .addCase(deleteKycDocument.fulfilled, (state, action) => {
        const { userId, docId } = action.payload;
        if (state.uploadedKyc[userId]) {
          state.uploadedKyc[userId] = state.uploadedKyc[userId].filter(
            (doc) => doc.id !== docId
          );
        }
      });
  },
});

export default CustomerKycSlice.reducer;
